import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  canPushAnalyticsToDataLayer,
  GTM_READY_EVENT,
  pushVirtualPageView,
  readStoredConsent,
} from 'config/analyticsConfig';
import { clearGaUserIdentity, setGaUserIdentity } from 'config/analyticsIdentity';
import { captureUtmFromLocation } from 'config/analyticsUtm';
import useMeQuery from 'hooks/useMeQuery/useMeQuery';
import authManager from 'utils/authManager/authManager';

/**
 * F1.2 — virtual page_view on SPA routes (incl. first paint after GTM + consent).
 * F1.4 — capture utm_* into sessionStorage.
 *
 * Google Tag also loads on the app (linker + user properties) with send_page_view off.
 * The first hit still comes from this tracker via `virtual_page_view`.
 * When the visitor is logged in, that hit waits until `/me` is loaded so
 * `user_id` and `subscription_status` are already set.
 */
const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const lastSentKeyRef = useRef<string | null>(null);
  const [gtmEpoch, setGtmEpoch] = useState(0);
  const loggedIn = Boolean(authManager.getCookieAuthData()?.token);
  const { data: me, isSuccess } = useMeQuery({ enabled: loggedIn });

  useEffect(() => {
    const onReady = () => setGtmEpoch(n => n + 1);
    window.addEventListener(GTM_READY_EVENT, onReady);
    if (window.__gtmLoaded) onReady();
    return () => window.removeEventListener(GTM_READY_EVENT, onReady);
  }, []);

  useEffect(() => {
    if (!readStoredConsent()?.analytics) return;
    if (!loggedIn) {
      clearGaUserIdentity();
      return;
    }
    if (!isSuccess || !me) return;
    setGaUserIdentity({
      id: me.id,
      isSchoolAccount: me.isSchoolAccount,
      hasPremiumAccess: me.hasPremiumAccess,
    });
  }, [loggedIn, isSuccess, me, gtmEpoch]);

  useEffect(() => {
    captureUtmFromLocation(location.search);

    if (!canPushAnalyticsToDataLayer()) return;
    if (!readStoredConsent()?.analytics) return;
    if (loggedIn && !isSuccess) return;

    const key = `${location.pathname}${location.search}`;
    if (lastSentKeyRef.current === key) return;
    lastSentKeyRef.current = key;
    pushVirtualPageView(location.pathname, location.search);
  }, [location.pathname, location.search, gtmEpoch, loggedIn, isSuccess]);

  return null;
};

export default AnalyticsRouteTracker;
